// src/App.js
import axios from 'axios';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import './firebase'; // Initializes Firebase client SDK
import React, { useEffect, useState } from 'react';
import { useUser, useAuth, SignIn } from '@clerk/clerk-react';
import ThoughtList from './ThoughtList';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider as MaterialTailwindProvider } from '@material-tailwind/react';
import CreateThoughtModal from './CreateThoughtModal';
import { loadUserThreads, saveUserThreads } from './firebase';
import { createUserIfNeeded } from './utils/firestoreUser';

// 1) Choose the correct backend URL:
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_BASE_URL   // e.g. "https://…vercel.app/api"
    : 'http://localhost:3001';             // local‐dev server who knows just need an update

console.log('▶▶▶ API_BASE_URL is:', API_BASE_URL);

// 2) Exchange Clerk JWT for Firebase custom token:
async function signInToFirebaseWithClerk(getToken) {
  const jwt = await getToken();
  const res = await axios.post(
    `${API_BASE_URL}/session/firebase`,
    {}, // no body needed
    { headers: { Authorization: `Bearer ${jwt}` } }
  );
  console.log("✅ custom token response:", res.data);
  // match whatever your serverless function returns:
  const firebaseToken = res.data.token;
  const auth = getAuth();
  await signInWithCustomToken(auth, firebaseToken);
  console.log("🔒 signed in as Firebase UID:", auth.currentUser.uid);
}

function App() {
  const [threads, setThreads] = useState([
    {
      selectedTags: [],
      selectedGroupId: null,
      filterMatchMode: 'any',
      filterSource: null,
      allTags: [],
      favoriteTags: [],
    },
  ]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hasLoadedThreads, setHasLoadedThreads] = useState(false);
  const [userReady, setUserReady] = useState(false);

  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();

  // Expose getToken() to window for debugging (so you can call window.__getToken() in console)
  useEffect(() => {
    window.__getToken = getToken;
  }, [getToken]);

  // console.log('🔑 Clerk UID:', user?.id);

  // 3) As soon as Clerk has loaded and signed in, sign into Firebase and create user
  useEffect(() => {
    async function ensureUser() {
      if (isLoaded && isSignedIn && user?.id) {
        await signInToFirebaseWithClerk(getToken);
        const ready = await createUserIfNeeded({
          id: user.id,
          email: user.emailAddresses?.[0]?.emailAddress || '',
          fullName: user.fullName || '',
        });
        setUserReady(ready);
      }
    }
    ensureUser();
  }, [isLoaded, isSignedIn, user, getToken]);

  // 4) Once our Firebase “user” is in place, load that user’s saved threads
  useEffect(() => {
    async function loadThreads() {
      if (!userReady || !isSignedIn || !user?.id) return;

      try {
        const savedThreads = await loadUserThreads(user.id);
        if (Array.isArray(savedThreads) && savedThreads.length > 0) {
          const patched = savedThreads.map((t) => {
            if (t.filterSource === 'group') {
              return {
                ...t,
                selectedGroupName: t.selectedGroupName || 'Group Thread',
                selectedTags: t.selectedTags || [],
                filterMatchMode: t.filterMatchMode || 'any',
              };
            }
            return t;
          });
          setThreads(patched);
        } else {
          setThreads([
            {
              selectedTags: [],
              selectedGroupId: null,
              filterMatchMode: 'any',
              filterSource: null,
              allTags: [],
              favoriteTags: [],
            },
          ]);
        }
      } catch (error) {
        console.error('Error loading threads:', error);
      } finally {
        setHasLoadedThreads(true);
      }
    }
    loadThreads();
  }, [user, isSignedIn, userReady]);

  // 5) Persist threads whenever they change
  useEffect(() => {
    if (user?.id && hasLoadedThreads) {
      saveUserThreads(user.id, threads);
    }
  }, [user, threads, hasLoadedThreads]);

  // 6) Render:
  return (
    <MaterialTailwindProvider>
      <div>
        {!isLoaded ? (
          <div className="flex items-center justify-center h-screen">
            <span className="text-gray-500 text-lg">Loading...</span>
          </div>
        ) : !isSignedIn ? (
          <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <SignIn routing="hash" />
          </div>
        ) : (
          <div className="flex flex-col h-screen bg-white">
            <Toaster position="bottom-center" />
            <div
              className="flex flex-row flex-grow overflow-x-auto"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.types.includes('application/json')) {
                  const raw = e.dataTransfer.getData('application/json');
                  if (raw) {
                    const data = JSON.parse(raw);
                    const newThread = {
                      selectedTags: data.tags || [],
                      selectedGroupName: data.name,
                      selectedGroupId: data.id || null,
                      filterMatchMode: data.matchAll ? 'all' : 'any',
                      filterSource: 'group',
                      allTags: [],
                      favoriteTags: [],
                    };
                    const alreadyExists = threads.some(
                      (t) =>
                        t.filterSource === 'group' &&
                        t.selectedGroupName === data.name
                    );
                    if (!alreadyExists) {
                      setThreads([...threads, newThread]);
                    }
                  }
                }
              }}
            >
              {threads.map((thread, index) => (
                <div key={index} className="relative">
                  {index > 0 && (
                    <button
                      className="absolute top-3 right-6 text-sm text-gray-500 hover:text-red-600 z-10 bg-white rounded-full px-2 py-1 shadow"
                      onClick={() => {
                        const updated = threads.filter((_, i) => i !== index);
                        setThreads(updated);
                      }}
                      title="Close Thread"
                    >
                      ✕
                    </button>
                  )}
                  <ThoughtList
                    user={user}
                    thread={thread}
                    threads={threads}
                    setThreads={setThreads}
                    threadIndex={index}
                    allTags={thread.allTags}
                    favoriteTags={thread.favoriteTags}
                    setShowCreateModal={index === 0 ? setShowCreateModal : undefined}
                  />
                </div>
              ))}
            </div>
            {showCreateModal && (
              <CreateThoughtModal
                onClose={() => setShowCreateModal(false)}
                threads={threads}
                setThreads={setThreads}
                threadIndex={0}
              />
            )}
          </div>
        )}
      </div>
    </MaterialTailwindProvider>
  );
}

export default App;